<template>
  <div class="shop-the-look critical-component-clear">
    <div
      :class="$blockClass(blockClass, 'shop-the-look-wrapper')"
      class="shop-the-look__wrapper"
    >
      <btn
        class="shop-all-items-button"
        type="button"
        :icon="false"
        :label="shopAllRoomItemsText"
        :state="state === 'adding'"
        state-icon="misc.loading"
        @click="handleAddProductsToCart"
      />
      <responsive-image
        v-if="image.src && image.width && image.height"
        :image="image"
        :ratio="`${image.width}:${image.height}`"
        class="shop-the-look__image"
        :force-update="true"
        :min-max="imageMinMax"
        :lazyload="false"
        :sizes="'(min-width: 1440px) 1392px, 100vw'"
      />

      <template
        v-for="(block, tagIndex) in filteredShopTagBlocks"
        :key="block.id"
      >
        <shop-tag
          :product="block.product"
          :chip-position="block.settings.chip_position"
          :coordinates="getCoordinates(block.settings)"
          :index="tagIndex + 1"
          :active-index="activeTagIndex"
          :overlay-namespace="overlayNamespace"
          :variant="block.variant"
          @selected-product="updateSelectedProduct"
          @update-active-index="index => activeTagIndex = index"
          @mouseover="onTagHover(tagIndex)"
          @mouseleave="onSwiperItemMouseLeave"
        />
      </template>
    </div>

    <div class="shop-the-look__swipper">
    <swiper
      :slides-per-view="isMobile ? '1.3' : 'auto'"
      :modules="modules"
      :navigation="navigation"
      :free-mode="!isMobile"
      :mousewheel="mousewheel"
      :space-between="20"
      @swiper="onSwiper"
      @slideChange="onSwiperSlideChange"
    >
      <swiper-slide
        v-for="(block, tagIndex) in filteredShopTagBlocks"
        :key="block.id"
        class="shop-the-look__swiper-slide"
      >
          <product-chip

              :isActive="activeTagIndex === tagIndex + 1"
              :image="block.variantImage"
              :product-title="block.product.title"
              :show-price="true"
              :show-variant-title="false"
              :variant="block.variant"
              @mouseover="onSwiperItemHover(tagIndex)"
              @mouseleave="onSwiperItemMouseLeave"
              @click="onSwiperItemClick(block, tagIndex)"
            >
              <template v-slot:absoluteElement>
                <transition name="fade">
                  <span
                    v-if="activeTagIndex === tagIndex + 1"
                    class="shop-tag__plus"
                    v-html="$icon('enclosed.plus')"
                  />
                </transition>
              </template>
            </product-chip>

      </swiper-slide>

      <transition name="fade">
        <div
          v-if="swiper && !swiper.isEnd"
          class="shop-the-look__fade-effect-div fade-end" />
      </transition>
      <transition name="fade">
        <div
          v-if="swiper && !swiper.isBeginning"
          class="shop-the-look__fade-effect-div fade-beginning" />
      </transition>
    </swiper>

    <div class="shop-the-look__swiper__actions">
      <button
        v-show="!isMobile"
        :class="`shop-the-look__swiper__navigation shop-the-look__swiper__navigation--prev shop-the-look__swiper__navigation--prev-${swiperId}`"
        :aria-label="'Previous slide'"
      >
        <span
          class="button__icon button-icon"
          v-html="$icon('enclosed.chevron.left')"
        />
      </button>

      <button
        v-show="!isMobile"
        :class="`shop-the-look__swiper__navigation shop-the-look__swiper__navigation--next  shop-the-look__swiper__navigation--next-${swiperId}`"
        :aria-label="'Next slide'"
      >
        <span
          class="button__icon button-icon"
          v-html="$icon('enclosed.chevron.right')"
        />
      </button>
    </div>
    </div>

    <div
      v-if="false && shopTagBlocks.length"
      :class="$blockClass(blockClass, 'mobile-chip')"
      class="shop-the-look__chip"
      @click="showDrawer"
    >
      <product-chip
        :image="variantImage"
        :product-title="product.title"
        :show-price="true"
        :show-variant-title="false"
        :variant="variant"
      />
      <span
        :class="$blockClass(blockClass, 'shop-tag-plus')"
        class="shop-tag__plus"
        v-html="$icon('enclosed.plus')"
      />
    </div>
  </div>

  <overlay
    ref="overlay"
    :namespace="overlayNamespace"
    block-class="product-form-overlay"
    :direction="isMobile ? 'up' : 'left'"
    type="drawer"
    @scroll:update="updateAddToCartVisibility"
  >
    <template #headercontent>
      <transition name="fade">
        <btn
          v-show="showMiniAtc"
          :class="[
            {'button--outlined klaviyo-bis-trigger': !variant.available && bisEnabled},
            {'button--secondary': variant.available},
          ]"
          :disabled="!variant.available && !bisEnabled"
          :icon="false"
          :label="addToCartLabel"
          :state="atcLoading"
          block-class="shop-the-look"
          modifiers="small"
          state-icon="misc.loading"
          @click="addToCart"
          @touchstart="loadComponent('klaviyo-bis')"
        />
      </transition>
    </template>
    <template #body>
      <div class="container">
        <main-product
          v-cloak
          v-if="getComponentVisible('main-product')"
          :accordions="false"
          :breadcrumbs="false"
          :mobile="true"
          :product="product"
          :personalization="product?.template === 'personalizable'"
          :update-url="false"
          :variant="variant"
          @update-request-object="updateSelectedVariant"
        >
          <template #form>
            <slot name="shoppay" />
            <span
              class="visually-hidden"
              data-cart-subtotal
              v-text="variantPrice"
            />
          </template>
        </main-product>
      </div>
    </template>
  </overlay>
</template>

<script>

/**
 * Vue: Shop the look (shop-the-look)
 * -----------------------------------------------------------------------------
 * An image with shop tags that can be clicked to open a product form.
 *
 * @param {String} blockClass - Block class to apply to elements in this
 * component.
 * @param {Object[]} shopTagBlocks - Blocks from liquid that contains settings.
 * @param {String} shopTagBlocks.settings.chip_position - top, right, bottom,
 * or left
 * @param {Number} shopTagBlocks.settings.desktop_x - Horizontal desktop
 * position for shop tag.
 * @param {Number} shopTagBlocks.settings.desktop_y - Vertical desktop
 * position for shop tag.
 * @param {Number} shopTagBlocks.settings.mobile_x - Horizontal mobile
 * position for shop tag.
 * @param {Number} shopTagBlocks.settings.mobile_y - Vertical mobile position
 * for shop tag.
 * @param {String} shopTagBlocks.settings.product - Product handle
 * @param {String} shopTagBlocks.settings.variant_title - Optional variant title
 * to display if the default is not desired.
 * @param {Object} desktopImage - An image object with src, width, height,
 * and alt text.
 * @param {Number} index - A unique index if shop-the-look component is used
 * more than once in a section.
 * @param {String} layout - Optional. The specific image and coordinates to
 * display if split coordinates are not desired. Can be "desktop, mobile, or
 * none".
 * @param {Object} mobileImage - An image object with src, width, height,
 * and alt text.
 * @param {String} sectionId - The section ID from liquid.
 */

import { mapActions, mapState } from 'vuex'

import ShopTag from '~global/shop-tag/shop-tag'
import MainProduct from '~async/main-product/main-product'
import ProductChip from '~async/bed-visualizer/product-chip'
import Btn from '~global/btn/btn'
import { isVisible } from '~/helpers/general'
import { getVariantByTitle, getVariantImage, isPreorder } from '~/helpers/product'
import { queryProducts } from '~/helpers/query'
import timings from '~/config/timings'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Pagination, Thumbs, FreeMode, Mousewheel, Navigation } from 'swiper'

export default {
  name: 'ShopTheLook',

  components: {
    ShopTag,
    MainProduct,
    ProductChip,
    Btn,
    Swiper,
    SwiperSlide,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    shopTagBlocks: {
      type: Array,
      default: () => ([]),
    },
    desktopImage: {
      type: Object,
      default: () => ({}),
    },
    imageMinMax: {
      type: String,
      default: '720-2880',
    },
    index: {
      type: Number,
      default: 0,
    },
    layout: {
      type: String,
      default: 'none',
    },
    mobileImage: {
      type: Object,
      default: () => ({}),
    },
    sectionId: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      shopAllRoomItemsText: 'Add All To Cart',
      shopAllRoomItemsArray: [],
      activeTagIndex: -1,
      atcLoading: false,
      bisEnabled: this.$variable('klaviyo.bisEnabled'),
      showMiniAtc: false,
      productFormAddToCart: null,
      products: {},
      selectedProduct: {
        product: false,
        variant: false,
      },
      ready: false,
      timings,
      swiper: null,
      state: '',
    }
  },

  setup(props) {

    const swiperId = crypto.randomUUID();
    const modules = [
      Pagination,
      Thumbs,
      FreeMode,
      Mousewheel,
      Navigation,
    ]

    const mousewheel = {
      forceToAxis: true,
      enabled: true,

    }

    const navigation = {
      nextEl: '.shop-the-look__swiper__navigation--next-' + swiperId,
      prevEl: '.shop-the-look__swiper__navigation--prev-' + swiperId,
    }

    return {
      modules,
      mousewheel,
      navigation,
      swiperId,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Small add to cart button label
     */
    addToCartLabel() {
      if (!this.variant.available) {
        if (this.bisEnabled) {
          return this.$string('backInStock.notify_me')
        }
        return this.$string('product.cta.sold_out')
      }

      if (isPreorder(this.product, this.variant)) {
        return this.$string('product.cta.pre_order')
      }

      return this.$string('product.cta.add_to_cart')
    },

    /**
     * Compute the image to display for desktop vs mobile.
     * @returns {*}
     */
    image() {
      let imageLayout = this.isMobile ? 'mobile' : 'desktop'
      if (this.layout !== 'none') {
        imageLayout = this.layout
      }
      return this[imageLayout + 'Image']
    },

    /**
     * Compute the unique, dynamic overlay namespace.
     * @returns {string}
     */
    overlayNamespace() {
      return `productFormDrawer-${this.sectionId}-${this.index}`
    },

    /**
     * Compute the product handles from liquid settings.
     * @returns {String[]}
     */
    productHandles() {
      return this.shopTagBlocks.map(block => block.settings.product)
    },

    /**
     * Compute the product
     * @returns {Object}
     */
    product() {
      return this.selectedProduct.product
    },

    /**
     * Compute the variant
     * @returns {Object}
     */
    variant() {
      return this.selectedProduct.variant
    },

    /**
     * Compute the current variant image.
     * @returns {Object}
     */
    variantImage() {
      return getVariantImage(this.product, this.variant)
    },

    /**
     * Compute the variant price.
     * @returns {String}
     */
    variantPrice() {
      const price = this.variant?.price?.amount
      return this.$formatMoney(price || 0)
    },

    /**
     * Filtered tags
     */
    filteredShopTagBlocks() {
      const shopTags =  this.shopTagBlocks
        ?.filter(x => !!this.products[x.settings.product]?.available)
        .map(x => {
          const variant = this.getVariant(x.settings.product, x.settings.variant_title)
          const product = this.products[x.settings.product]
          return {
            ...x,
            product,
            variant,
            variantImage: getVariantImage(product, variant),
          }
        })

      return shopTags
    },
  },

  mounted() {
    this.queryProducts()
    setTimeout(() => {
      this.ready = true
    }, timings.l)
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      closeOverlay: 'overlays/close',
      loadComponent: 'components/load',
      openOverlay: 'overlays/open',
    }),

    /**
     * Add item to cart on clicking the small add to cart button
     */
    async addToCart() {
      this.atcLoading = true
      if (!this.variant) {
        this.atcLoading = false
        return
      }

      if (this.bisEnabled && !this.variant.available) {
        this.atcLoading = false
        this.openKlaviyoBiS()
        return
      }

      try {
        await this.addItem([
          {
            id: this.variant.id,
            quantity: 1,
            variant: this.variant,
            product: this.product,
          },
        ])

        this.openOverlay({
          component: 'cart-drawer',
          ignoreDismissed: true,
          namespace: 'cartDrawer',
        })

        this.atcLoading = false

        this.$gtmTrack('AddtoCart', this.$variable('page.title'))
      }
      catch (error) {
        this.atcLoading = false
        reportError('Failed to add product to cart from shop the look mobile (top button)', error)
      }
    },

    /**
     * Format coordinates to be used in shop tag.
     * @param settings - The liquid block settings.
     * @returns {{desktop: {x: *, y: *}, mobile: {x: *, y: *}}}
     */
    getCoordinates(settings) {
      let mobile = 'mobile_', desktop = 'desktop_'
      if (this.layout !== 'none') {
        mobile = desktop = this.layout + '_'
      }
      return {
        mobile: {
          x: settings[mobile + 'x'],
          y: settings[mobile + 'y'],
        },
        desktop: {
          x: settings[desktop + 'x'],
          y: settings[desktop + 'y'],
        },
      }
    },

    /**
     * Find a specific variant based on title
     * @param handle - The product handle.
     * @param variantTitle - The variant title to find.
     * @returns {Object|Boolean}
     */
    getVariant(handle, variantTitle) {
      const product = this.products[handle]

      if (!product?.variants) {
        return false
      } else if (!variantTitle) {
        return product.variants[0]
      }

      return getVariantByTitle(product, null, variantTitle)
    },

    openKlaviyoBiS() {
      this.openOverlay({
        component: 'klaviyo-bis',
        ignoreDismissed: true,
        namespace: 'klaviyoBis',
      })
      cnvs.EventBus.emit('klaviyoBis:variantUpdated', {
        product: this.product,
        variant: this.variant,
      })
    },

    /**
     * Show the PDP in a drawer.
     */
    showDrawer() {
      this.openOverlay({
        component: 'main-product',
        ignoreDismissed: true,
        namespace: this.overlayNamespace,
      })
      setTimeout( () => {
        this.updateAddToCartVisibility()
      }, timings.l)
    },

    /**
     * Update the small add to cart button visibility at the top of mobile
     * drawer overlay. It should be visible if the form button is out of view.
     */
    updateAddToCartVisibility() {
      const overlayComponent = this.$refs.overlay
      const overlay = overlayComponent.$refs?.overlay
      if (!this.productFormAddToCart && overlay) {
        this.productFormAddToCart = overlay.querySelector('[js-product-form="addToCart"]')
      }
      this.showMiniAtc = !isVisible(this.productFormAddToCart, 20)
    },

    /**
     * Update the selected product and variant.
     * @param payload.product - Canvas product
     * @param payload.variant - Canvas variant
     */
    updateSelectedProduct(payload) {
      this.selectedProduct.product = payload.product
      this.selectedProduct.variant = payload.variant
    },

    /**
     * Update the current variant based on the selected variant from the
     * drawer product form.
     * @param payload
     */
    updateSelectedVariant(payload) {
      if (payload.type !== 'variant') {
        return
      }
      this.selectedProduct.variant = payload.response
    },

    /**
     * Get Canvas formatted GraphQL products from handles.
     */
    async queryProducts() {
      const handles = this.productHandles

      if (!handles.length) {
        return
      }

      const products = await queryProducts(handles)
      products.forEach(product => {
        this.products[product.handle] = {
          ...product,
          template: this.shopTagBlocks.find(block => block.settings.product === product.handle)?.product?.template,
        }
      })

      this.shopAllRoomItemsArray = Object.values(JSON.parse(JSON.stringify(products))).flat()

      this.shopAllRoomItemsArray = this.shopAllRoomItemsArray.map((product)=>{
        let variantBlock = this.shopTagBlocks.find(block => block.settings.product === product.handle)
        return {
          ...product,
          variantTitle: variantBlock.settings.variant_title,
        }
      })
    },

    handleAddProductsToCart(){
      this.handleAddToCart()
    },

    /**
     * Handle add to cart submission.
     */
     async handleAddToCart() {
      clearTimeout(this.timeout)
      this.error = ''
      this.state = 'adding'

      const items = Object.values(this.shopAllRoomItemsArray).flat()

      let newItems = items.filter(item => item.available !== false)

      newItems = newItems.map((product)=>{
        const vId = this.getVariant(product.handle, product.variantTitle)
        return {
          ...product,
          selectedVariantId: vId ? vId.id : null,
          selectedVariantAvailability: vId ? vId.available : null,
        }
      })

      newItems = newItems.filter(item => item.selectedVariantAvailability !== false && item.selectedVariantAvailability !== null && this.products[item.handle].template !== 'personalizable')

      console.log('These are the items to be added -> ', newItems)

      try {
        await this.addItem(newItems.map((item) => ({
          id: item.selectedVariantId || item.variants.filter(variant => variant.available !== false)[0].id,
          quantity: 1,
          variant: item.selectedVariantId || item.variants.filter(variant => variant.available !== false)[0],
          product: item,
        })))

        this.state = ''

        this.openOverlay({
          component: 'cart-drawer',
          ignoreDismissed: true,
          namespace: 'cartDrawer',
        })

        this.atcLoading = false

        this.$gtmTrack('AddAllToCart', this.$variable('page.title'))

      }
      catch (error) {
        this.state = 'error'

        if (error.length) {
          this.error = error
          return
        }

        console.error('Failed to add product to cart', error)
      }
    },

    /**
     * On swiper init
     */
    onSwiper(swiper) {
      this.swiper = swiper
    },

    /**
     * On tag hover, we slide the swiper to the current
     * tag
     */
    onTagHover(tagIndex) {
        this.activeTagIndex = tagIndex + 1
        if (this.swiper) {
          this.swiper.slideTo(tagIndex)
        }
    },

    onSwiperItemHover(tagIndex)
    {
      if (!this.isMobile) {
        this.activeTagIndex = tagIndex + 1
      }
    },

    onSwiperItemMouseLeave()
    {
      if (!this.isMobile) {
        this.activeTagIndex = -1
      }
    },

    onSwiperItemClick(block, tagIndex) {
      this.updateSelectedProduct(block)
      this.showDrawer()
      this.activeTagIndex = tagIndex + 1
    },

    onSwiperSlideChange(swiper) {
      if (this.isMobile) {
        this.activeTagIndex = swiper.activeIndex + 1
      }
    },
  },
}
</script>

<style lang="scss">
@import './shop-the-look';
</style>
